import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { idpConfigurationApi } from '../../../services/api/idpConfigurationApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ItemTitle, ItemDetailsGrid, OrderBadge, ActionButtons, ItalicText, DetailRow, Label } from '../styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import IconBrowser from '../../Permissions/Roles/RoleDetails/IconBrowser';
import { DynamicIcon } from '../../Permissions/Roles/utils/iconMap';
import { TRACKING_MODES, TRACKING_MODE_OPTIONS, getTrackingModeLabel } from '../../../constants/trackingModes';

const MeasurementType = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    name: '',
    description: '',
    usageGuideline: '',
    trackingMode: TRACKING_MODES.NONE,
    order: 0,
    color: '',
    icon: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openIconBrowser, setOpenIconBrowser] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await idpConfigurationApi.getAllMeasurementTypes();
      setItems(data);
    } catch (err) {
      showAlert('Failed to load measurement types.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setIsEditing(true);
    } else {
      setCurrentItem({
        name: '',
        description: '',
        usageGuideline: '',
        trackingMode: TRACKING_MODES.NONE,
        order: items.length + 1,
        color: '',
        icon: ''
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: type === 'checkbox' ? checked : (name === 'order' ? parseInt(value, 10) || 0 : value)
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await idpConfigurationApi.updateMeasurementType(currentItem.id, currentItem);
        showAlert('Measurement type updated successfully', 'success');
      } else {
        await idpConfigurationApi.createMeasurementType(currentItem);
        showAlert('Measurement type created successfully', 'success');
      }
      handleCloseDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} measurement type.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await idpConfigurationApi.deleteMeasurementType(currentItem.id);
      showAlert('Measurement type deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || 'Failed to delete measurement type.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    item.description?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/idp-configuration')}>
            <FaArrowLeft /> Back to IDP Configuration
          </BackButton>
          <Title>Measurement Type Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Measurement Types"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Type
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredItems.length}
            </span>
            {filteredItems.length === 1 ? 'type' : 'types'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No measurement types found"
              description={searchTerm ?
                "No measurement types match your search criteria." :
                "No measurement types have been added yet. Click the '+ Add Type' button to add one."}
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <ItemTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.icon && (
                          <Box sx={{ color: item.color || '#6b7280' }}>
                            <DynamicIcon iconName={item.icon} sx={{ fontSize: 20 }} />
                          </Box>
                        )}
                        {item.name}
                      </Box>
                      <OrderBadge>
                        Order: {item.order}
                      </OrderBadge>
                    </ItemTitle>
                    <ItemDetailsGrid>
                      {item.description ? item.description : <ItalicText>No description provided</ItalicText>}
                    </ItemDetailsGrid>
                    {item.usageGuideline && (
                      <Box sx={{ mb: 0.5 }}>
                        <Label style={{ display: 'block', marginBottom: '4px' }}>Usage:</Label>
                        <Box sx={{ fontSize: '14px', color: '#666', whiteSpace: 'pre-line' }}>
                          {item.usageGuideline}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
                      <Chip
                        size="small"
                        label={getTrackingModeLabel(item.trackingMode)}
                        variant="outlined"
                        sx={{
                          borderColor: item.color || '#6b7280',
                          color: item.color || '#6b7280',
                        }}
                      />
                    </Box>
                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(item)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Measurement Type' : 'Add Measurement Type'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name (e.g., Checklist, Progress, RAG)"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={currentItem.description}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="usageGuideline"
            label="Usage Guideline"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentItem.usageGuideline}
            onChange={handleInputChange}
            helperText="When should this measurement type be used?"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Tracking Mode</InputLabel>
            <Select
              name="trackingMode"
              value={currentItem.trackingMode || TRACKING_MODES.NONE}
              onChange={handleInputChange}
              label="Tracking Mode"
            >
              {TRACKING_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="color"
            label="Color"
            type="color"
            fullWidth
            variant="outlined"
            value={currentItem.color || '#6b7280'}
            onChange={handleInputChange}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Icon
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setOpenIconBrowser(true)}
              startIcon={
                currentItem.icon ? (
                  <DynamicIcon iconName={currentItem.icon} sx={{ color: currentItem.color || '#6b7280' }} />
                ) : (
                  <CategoryIcon />
                )
              }
              sx={{
                height: 56,
                width: '100%',
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: 'text.primary',
              }}
            >
              {currentItem.icon || 'Select Icon'}
            </Button>
          </Box>
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentItem.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentItem.name.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the measurement type "{currentItem.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Browser Dialog */}
      <IconBrowser
        open={openIconBrowser}
        onClose={() => setOpenIconBrowser(false)}
        selectedIcon={currentItem.icon}
        onIconSelect={(iconName) => setCurrentItem({ ...currentItem, icon: iconName })}
      />
    </Container>
  );
};

export default MeasurementType;
